// @flow
import {useRef, useEffect, useState, useCallback} from 'react';
import {useLocation} from 'react-router-dom';
import {debounce} from '../functions';

export * from './className';

export function usePrevious(value: any) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function useSafePrevious<T>(value: T): T | null {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

type InitialValueType<T> = T | ((prev?: T) => T);

type Options<T> = {
  cursor: number,
  history: T[],
  undo: () => void,
  redo: () => void,
};

export function useStateHistory<T>(
  initialState: InitialValueType<T>
): [T, (value: InitialValueType<T>) => void, Options<T>] {
  const [innerState, setInnerState] = useState<T>(initialState);
  const stateHistory = useRef<T[]>(
    // $FlowFixMe
    typeof initialState === 'function' ? [initialState()] : [initialState]
  );
  const cursor = useRef<number>(0);

  const setState = useCallback(
    (state: InitialValueType<T>) => {
      let valueToAdd: T =
        // $FlowFixMe
        typeof state === 'function' ? state(innerState) : state;

      if (stateHistory.current.length > cursor.current + 1) {
        stateHistory.current = stateHistory.current.slice(
          0,
          cursor.current + 1
        );
      }
      stateHistory.current.push(valueToAdd);
      cursor.current = stateHistory.current.length - 1;
      setInnerState(valueToAdd);
    },
    [setInnerState, innerState]
  );

  const undo = useCallback(() => {
    if (cursor.current <= 0) return;
    cursor.current--;
    setInnerState(stateHistory.current[cursor.current]);
  }, []);

  const redo = useCallback(() => {
    if (cursor.current >= stateHistory.current.length - 1) return;
    cursor.current++;
    setInnerState(stateHistory.current[cursor.current]);
  }, []);

  return [
    innerState,
    setState,
    {
      cursor: cursor.current,
      history: stateHistory.current,
      undo,
      redo,
    },
  ];
}

export function useDebounce(value: any, debounceTime: number = 1000) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setV(value);
    }, debounceTime);
    return () => clearTimeout(handler);
  }, [value, debounceTime, v]);
  return v;
}

export function ScrollToTop() {
  const {pathname} = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

export function useDebounceEffect(
  callback: () => any,
  delay: number,
  deps: any[]
) {
  const innerCallback = useCallback(callback, deps);

  useEffect(() => {
    const handler = setTimeout(() => innerCallback(), delay);
    return () => clearTimeout(handler);
  }, [delay, innerCallback]);
}

type KeyOptions = {
  key: string,
  ctrlKey?: boolean,
  shiftKey?: boolean,
  altKey?: boolean,
  metaKey?: boolean,
};

export function useOnKeyPress(options: KeyOptions, callback: () => any) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    function handle(event) {
      const {ctrlKey, shiftKey, altKey, metaKey, key} = options;

      if (event.key !== key) return;

      if (ctrlKey && !event.ctrlKey) return;
      if (shiftKey && !event.shiftKey) return;
      if (altKey && !event.altKey) return;
      if (metaKey && !event.metaKey) return;

      callbackRef.current();
    }
    window.document.addEventListener('keypress', handle);
    return () => {
      window.document.removeEventListener('keypress', handle);
    };
  }, [options]);
}

type UseNearScreenParams = {
  distance?: string,
  externalRef?: any,
  once?: boolean,
  root?: any,
  threshold?: number,
  useAppRoot?: boolean,
};

export function useNearScreen(params: UseNearScreenParams) {
  const {
    distance = '100px',
    externalRef,
    once = true,
    root,
    threshold,
    useAppRoot,
  } = params;

  const [isNearScreen, setIsNearScreen] = useState(false);
  const [fromRef, setRefElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const elementObserver = externalRef ? externalRef.current : fromRef;

    const onChange = ([element], observer) => {
      if (element.isIntersecting) {
        setIsNearScreen(true);
        if (once) {
          observer.disconnect();
        }
      } else if (!once) {
        setIsNearScreen(false);
      }
    };
    const rootElement = useAppRoot ? document.getElementById('main-app') : root;
    const observer = new IntersectionObserver(onChange, {
      root: rootElement || null,
      rootMargin: distance,
      threshold,
    });

    if (elementObserver) {
      observer.observe(elementObserver);
    }

    return () => observer.disconnect();
  }, [distance, externalRef, fromRef, once, root, threshold, useAppRoot]);

  return {isNearScreen, fromRef: setRefElement};
}

export function useToggle(
  initialState: boolean = false
): [boolean, () => void, (value: boolean) => void] {
  const [state, setState] = useState<boolean>(initialState);

  const toggle = useCallback(() => {
    setState(prevState => !prevState);
  }, []);

  return [state, toggle, setState];
}

export function useCreateObjectUrl(
  initialObject: null | File | Blob | MediaSource
) {
  const [objectURL, setObjectURL] = useState<null | string>(null);

  const [object, setObject] = useState<null | File | Blob | MediaSource>(
    initialObject
  );

  useEffect(() => {
    if (!object) {
      return;
    }
    let objectURL;
    try {
      objectURL = URL.createObjectURL(object);
      setObjectURL(objectURL);
    } catch (error) {
      console.error(error);
    }
    return () => {
      if (!objectURL) {
        return;
      }
      URL.revokeObjectURL(objectURL);
      setObjectURL(null);
    };
  }, [object]);

  return {
    objectURL,
    object,
    setObject,
  };
}

export function useCallbackDebounce(
  callback: Function,
  deps: any[],
  delay: number
) {
  return useCallback(debounce(callback, delay), deps);
}
